/**
 * useDraftManagement Hook
 * Fatura taslağı kaydetme/yükleme işlemlerini yönetir
 */

import { useState, useCallback, useEffect } from 'react';
import { DraftInvoiceData, Account, InvoiceItem } from '../components/types/invoice.types';

const DRAFT_STORAGE_KEY = 'salesInvoiceDraft';
const DRAFT_EXPIRY_DAYS = 7; // Taslak 7 gün sonra otomatik silinir

export const useDraftManagement = () => {
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [draftData, setDraftData] = useState<DraftInvoiceData | null>(null);

  /**
   * Taslak kaydet (localStorage)
   */
  const saveDraftToLocalStorage = useCallback(
    (data: {
      invoiceNumber: string;
      date: Date;
      dueDate: Date;
      notes: string;
      currency: string;
      exchangeRate: string;
      selectedAccount: Account | null;
      selectedItems: InvoiceItem[];
      tevkifatType?: string;
      tevkifatRate?: string;
      stopajRate?: number;
    }) => {
      try {
        // Kalemler yoksa kaydetme
        if (data.selectedItems.length === 0) {
          console.log('⚠️ [TASLAK] Kalem yok, taslak kaydedilmedi');
          return;
        }

        const timestamp = Date.now();
        
        // Özet bilgileri hesapla
        const itemCount = data.selectedItems.length;
        const totalAmount = data.selectedItems.reduce((sum, item) => sum + item.grossTotal, 0);
        
        const draft: DraftInvoiceData = {
          id: `draft_${timestamp}`, // Benzersiz ID
          invoiceNumber: data.invoiceNumber,
          date: data.date.toISOString(),
          dueDate: data.dueDate.toISOString(),
          notes: data.notes,
          currency: data.currency,
          exchangeRate: data.exchangeRate,
          selectedAccount: data.selectedAccount,
          selectedItems: data.selectedItems,
          tevkifatType: data.tevkifatType,
          tevkifatRate: data.tevkifatRate,
          stopajRate: data.stopajRate,
          timestamp,
          savedAt: new Date(timestamp).toISOString(),
          accountName: data.selectedAccount?.companyName,
          itemCount,
          totalAmount,
        };

        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
        console.log('💾 [TASLAK] Taslak kaydedildi');
      } catch (error) {
        console.error('❌ [TASLAK] Taslak kaydedilirken hata:', error);
      }
    },
    []
  );

  /**
   * Taslak yükle (localStorage'dan)
   */
  const loadDraftFromLocalStorage = useCallback((): DraftInvoiceData | null => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!savedDraft) {
        return null;
      }

      const draft: DraftInvoiceData = JSON.parse(savedDraft);

      // Taslak süre kontrolü
      const draftAge = Date.now() - draft.timestamp;
      const expiryTime = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      if (draftAge > expiryTime) {
        console.log('⚠️ [TASLAK] Taslak süresi dolmuş, siliniyor');
        clearDraftFromLocalStorage();
        return null;
      }

      console.log('✅ [TASLAK] Taslak yüklendi');
      return draft;
    } catch (error) {
      console.error('❌ [TASLAK] Taslak yüklenirken hata:', error);
      return null;
    }
  }, []);

  /**
   * Taslak temizle (localStorage'dan)
   */
  const clearDraftFromLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      console.log('🗑️ [TASLAK] Taslak temizlendi');
      setDraftData(null);
    } catch (error) {
      console.error('❌ [TASLAK] Taslak temizlenirken hata:', error);
    }
  }, []);

  /**
   * Sayfa yüklendiğinde taslak kontrolü yap
   */
  const checkForDraft = useCallback((): boolean => {
    const draft = loadDraftFromLocalStorage();
    if (draft) {
      setDraftData(draft);
      setIsDraftModalOpen(true);
      return true;
    }
    return false;
  }, [loadDraftFromLocalStorage]);

  /**
   * Taslak modal açık/kapalı
   */
  const handleOpenDraftModal = useCallback(() => {
    setIsDraftModalOpen(true);
  }, []);

  const handleCloseDraftModal = useCallback(() => {
    setIsDraftModalOpen(false);
  }, []);

  /**
   * Taslağı kabul et ve formu doldur
   */
  const acceptDraft = useCallback(
    (callbacks: {
      setInvoiceNumber: (value: string) => void;
      setDate: (value: Date) => void;
      setDueDate: (value: Date) => void;
      setNotes: (value: string) => void;
      setCurrency: (value: string) => void;
      setExchangeRate: (value: string) => void;
      setSelectedAccount: (value: Account | null) => void;
      setSelectedItems: (value: InvoiceItem[]) => void;
      setTevkifatType?: (value: string) => void;
      setTevkifatRate?: (value: string) => void;
      setStopajRate?: (value: number) => void;
      setShowTevkifat?: (value: boolean) => void;
      setShowStopaj?: (value: boolean) => void;
    }): boolean => {
      if (!draftData) {
        return false;
      }

      try {
        console.log('📥 [TASLAK] Taslak uygulanıyor...');

        callbacks.setInvoiceNumber(draftData.invoiceNumber);
        callbacks.setDate(new Date(draftData.date));
        callbacks.setDueDate(new Date(draftData.dueDate));
        callbacks.setNotes(draftData.notes);
        callbacks.setCurrency(draftData.currency);
        callbacks.setExchangeRate(draftData.exchangeRate);
        callbacks.setSelectedAccount(draftData.selectedAccount);
        callbacks.setSelectedItems(draftData.selectedItems);

        if (draftData.tevkifatType && callbacks.setTevkifatType) {
          callbacks.setTevkifatType(draftData.tevkifatType);
        }
        if (draftData.tevkifatRate && callbacks.setTevkifatRate) {
          callbacks.setTevkifatRate(draftData.tevkifatRate);
        }
        if (draftData.stopajRate !== undefined && callbacks.setStopajRate) {
          callbacks.setStopajRate(draftData.stopajRate);
        }

        // Tevkifat/Stopaj gösterim durumunu ayarla
        if (callbacks.setShowTevkifat) {
          callbacks.setShowTevkifat(!!draftData.tevkifatType);
        }
        if (callbacks.setShowStopaj) {
          callbacks.setShowStopaj((draftData.stopajRate || 0) > 0);
        }

        handleCloseDraftModal();
        console.log('✅ [TASLAK] Taslak başarıyla uygulandı');
        return true;
      } catch (error) {
        console.error('❌ [TASLAK] Taslak uygulanırken hata:', error);
        return false;
      }
    },
    [draftData, handleCloseDraftModal]
  );

  /**
   * Taslağı reddet ve sil
   */
  const rejectDraft = useCallback(() => {
    clearDraftFromLocalStorage();
    handleCloseDraftModal();
    console.log('🗑️ [TASLAK] Taslak reddedildi ve silindi');
  }, [clearDraftFromLocalStorage, handleCloseDraftModal]);

  return {
    // State
    isDraftModalOpen,
    draftData,

    // Actions
    saveDraftToLocalStorage,
    loadDraftFromLocalStorage,
    clearDraftFromLocalStorage,
    checkForDraft,
    handleOpenDraftModal,
    handleCloseDraftModal,
    acceptDraft,
    rejectDraft,
  };
};

